/**
 * 작성자 : 박승호
 * 작성날짜 : 2025.06.27
 * 수정날짜 : 2025.09.17
 * 기능 : 공통 아이콘 컴포넌트 [ 색상, 사이즈 커스텀 편하게 사용하기 위한 용도 ]
 */

type IconType = {
  size?: number;
  color?: string;
  colorType?: 'default' | 'dark';
  style?: React.CSSProperties;
};

const colorSet = {
  cool: 'var(--sky-400)', // 비, 눈 컬러
  coolDark: 'var(--blue-800)', // 비, 눈 어두운 컬러
  bright: 'var(--amber-400)', // 해, 번개 컬러
  cloud1: 'var(--zinc-300)', // 일반 구름 1,2,3 컬러
  cloud2: 'var(--zinc-200)',
  cloud3: '#EAEAEA',
  blur1: 'var(--zinc-500)', // 어두운 1,2,3 구름
  blur2: 'var(--zinc-400)',
  blur3: 'var(--zinc-300)',
};

// 날씨 - 맑음 아이콘
export const SunnySvgIcon = ({ size = 100, color = colorSet.bright, colorType = 'default', style }: IconType) => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width={size} viewBox='0 0 100 100' fill='none' style={style}>
      <circle cx='50.2703' cy='50.4999' r='22.1311' fill={color} />
      <rect x='48.3906' y='13' width='3.68852' height='8.60656' rx='1.84426' fill={color} />
      <rect x='48.3906' y='79.3934' width='3.68852' height='8.60656' rx='1.84426' fill={color} />
      <rect x='26.7007' y='21.246' width='3.68852' height='8.60656' rx='1.84426' transform='rotate(-36 26.7007 21.246)' fill={color} />
      <rect x='65.7256' y='74.9592' width='3.68852' height='8.60656' rx='1.84426' transform='rotate(-36 65.7256 74.9592)' fill={color} />
      <rect x='14' y='40.6658' width='3.68852' height='8.60656' rx='1.84426' transform='rotate(-72 14 40.6658)' fill={color} />
      <rect x='77.144' y='61.1826' width='3.68852' height='8.60656' rx='1.84426' transform='rotate(-72 77.144 61.1826)' fill={color} />
      <rect x='15.1396' y='63.8423' width='3.68852' height='8.60656' rx='1.84426' transform='rotate(-108 15.1396 63.8423)' fill={color} />
      <rect x='78.2837' y='43.3256' width='3.68852' height='8.60656' rx='1.84426' transform='rotate(-108 78.2837 43.3256)' fill={color} />
      <rect x='29.6846' y='81.9222' width='3.68852' height='8.60656' rx='1.84426' transform='rotate(-144 29.6846 81.9222)' fill={color} />
      <rect x='68.7095' y='28.2089' width='3.68852' height='8.60656' rx='1.84426' transform='rotate(-144 68.7095 28.2089)' fill={color} />
    </svg>
  );
};

// 날씨 - 흐림 아이콘
export const BlurSvgIcon = ({ size = 100, color, colorType = 'default', style }: IconType) => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width={size} viewBox='0 0 100 100' fill='none' style={style}>
      <rect x='18.75' y='41.9999' width='71.25' height='28.7499' rx='14.3749' fill={colorSet.blur3} />
      <path
        d='M51.875 17C67.408 17 80 29.0324 80 43.875C79.9999 58.5179 67.7443 70.4246 52.5 70.7422V70.75H31.25V62.1436C26.5965 57.3488 23.75 50.9301 23.75 43.875C23.75 29.0324 36.342 17 51.875 17Z'
        fill={colorSet.blur2}
      />
      <ellipse cx='31.25' cy='50.1248' rx='21.25' ry='20.6249' fill={colorSet.blur1} />
      <rect x='39.7505' y='63.1783' width='46.8214' height='18.8928' rx='9.44639' fill={colorSet.cloud3} />
      <path
        d='M61.5186 46.7498C71.7258 46.7499 80 54.6573 80 64.4109C79.9997 74.0333 71.9464 81.8566 61.9287 82.0652V82.071H47.9648V76.4148C44.9071 73.2641 43.0363 69.0468 43.0361 64.4109C43.0361 54.6572 51.3111 46.7498 61.5186 46.7498Z'
        fill={colorSet.cloud2}
      />
      <ellipse cx='47.9643' cy='68.5177' rx='13.9643' ry='13.5535' fill={colorSet.cloud1} />
    </svg>
  );
};

// 날씨 - 구름조금 아이콘
export const LittleCloudySvgIcon = ({ size = 100, color, colorType = 'default', style }: IconType) => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width={size} viewBox='0 0 100 100' fill='none' style={style}>
      <ellipse cx='64.2507' cy='45.7106' rx='16.3538' ry='16.3538' fill={colorSet.bright} />
      <rect x='62.8618' y='18' width='2.72563' height='6.35981' rx='1.36281' fill={colorSet.bright} />
      <rect x='62.8618' y='67.0615' width='2.72563' height='6.35981' rx='1.36281' fill={colorSet.bright} />
      <rect width='2.72563' height='6.3598' rx='1.36281' transform='matrix(0.809016 -0.587786 0.587784 0.809018 46.834 24.0934)' fill={colorSet.bright} />
      <rect width='2.72563' height='6.3598' rx='1.36281' transform='matrix(0.809016 -0.587786 0.587784 0.809018 75.6714 63.7848)' fill={colorSet.bright} />
      <rect width='2.72563' height='6.3598' rx='1.36282' transform='matrix(0.309016 -0.951057 0.951056 0.309018 37.4487 38.4437)' fill={colorSet.bright} />
      <rect width='2.72563' height='6.3598' rx='1.36282' transform='matrix(0.309016 -0.951057 0.951056 0.309018 84.1089 53.6044)' fill={colorSet.bright} />
      <rect width='2.72563' height='6.3598' rx='1.36282' transform='matrix(-0.309016 -0.951057 0.951056 -0.309018 38.291 55.5697)' fill={colorSet.bright} />
      <rect width='2.72563' height='6.3598' rx='1.36282' transform='matrix(-0.309016 -0.951057 0.951056 -0.309018 84.9517 40.409)' fill={colorSet.bright} />
      <rect width='2.72563' height='6.3598' rx='1.36281' transform='matrix(-0.809016 -0.587786 0.587784 -0.809018 49.0386 68.93)' fill={colorSet.bright} />
      <rect width='2.72563' height='6.3598' rx='1.36281' transform='matrix(-0.809016 -0.587786 0.587784 -0.809018 77.8765 29.2385)' fill={colorSet.bright} />
      <rect x='16.9814' y='56.7018' width='64.9901' height='26.2242' rx='13.1121' fill={colorSet.cloud3} />
      <path
        d='M47.1963 33.8981C61.3644 33.8982 72.8495 44.8733 72.8496 58.4117C72.8496 71.7685 61.6709 82.6291 47.7656 82.9186V82.9254H28.3828V75.0748C24.1384 70.7013 21.542 64.8468 21.542 58.4117C21.5421 44.8732 33.0281 33.8981 47.1963 33.8981Z'
        fill={colorSet.cloud2}
      />
      <ellipse cx='28.383' cy='64.1127' rx='19.383' ry='18.813' fill={colorSet.cloud1} />
    </svg>
  );
};

// 날씨 - 구름많음 아이콘
export const CloudySvgIcon = ({ size = 100, color, colorType = 'default', style }: IconType) => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width={size} viewBox='0 0 100 100' fill='none' style={style}>
      <rect x='17.9688' y='47.6252' width='73.0312' height='29.4688' rx='14.7344' fill={colorSet.cloud3} />
      <path
        d='M51.9219 22C67.8432 22 80.75 34.3331 80.75 49.5469C80.75 64.556 68.188 76.7604 52.5625 77.0859V77.0938H30.7812V68.2725C26.0114 63.3577 23.0938 56.7784 23.0938 49.5469C23.0938 34.3331 36.0006 22 51.9219 22Z'
        fill={colorSet.cloud2}
      />
      <ellipse cx='30.7812' cy='55.9533' rx='21.7812' ry='21.1407' fill={colorSet.cloud1} />
    </svg>
  );
};

// 날씨 - 비 아이콘
export const RainSvgIcon = ({ size = 100, color, colorType = 'default', style }: IconType) => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width={size} viewBox='0 0 100 100' fill='none' style={style}>
      <rect x='21.875' y='33.5001' width='64.125' height='25.875' rx='12.9375' fill={colorSet.cloud3} />
      <path
        d='M51.6875 11C65.6672 11 77 21.8291 77 35.1875C77 48.3662 65.9699 59.0823 52.25 59.3682V59.375H33.125V51.6289C28.9369 47.3136 26.375 41.537 26.375 35.1875C26.375 21.8291 37.7078 11 51.6875 11Z'
        fill={colorSet.cloud2}
      />
      <ellipse cx='33.125' cy='40.8125' rx='19.125' ry='18.5625' fill={colorSet.cloud1} />
      <path
        d='M35.5359 54.732C36.0882 53.7755 37.3114 53.4477 38.2679 54C39.2245 54.5523 39.5523 55.7755 39 56.732L31 70.5884C30.4477 71.545 29.2245 71.8728 28.2679 71.3205C27.3114 70.7682 26.9836 69.545 27.5359 68.5884L35.5359 54.732Z'
        fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark}
      />
      <path
        d='M51.7861 55.3349C52.3384 54.3783 53.5616 54.0506 54.5182 54.6029C55.4748 55.1551 55.8025 56.3783 55.2502 57.3349L42.7502 78.9855C42.1979 79.9421 40.9748 80.2699 40.0182 79.7176C39.0616 79.1653 38.7338 77.9421 39.2861 76.9855L51.7861 55.3349Z'
        fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark}
      />
      <path
        d='M53.5359 81.0526C54.0882 80.096 55.3114 79.7682 56.2679 80.3205C57.2245 80.8728 57.5523 82.096 57 83.0526L54.5 87.3827C53.9477 88.3393 52.7245 88.667 51.7679 88.1147C50.8114 87.5625 50.4836 86.3393 51.0359 85.3827L53.5359 81.0526Z'
        fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark}
      />
      <path
        d='M68.0359 55.9378C68.5882 54.9812 69.8114 54.6535 70.7679 55.2057C71.7245 55.758 72.0523 56.9812 71.5 57.9378L61 76.1243C60.4477 77.0809 59.2245 77.4087 58.2679 76.8564C57.3114 76.3041 56.9836 75.0809 57.5359 74.1243L68.0359 55.9378Z'
        fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark}
      />
    </svg>
  );
};

// 날씨 - 눈 아이콘
export const SnowSvgIcon = ({ size = 100, color, colorType = 'default', style }: IconType) => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width={size} viewBox='0 0 100 100' fill='none' style={style}>
      <rect x='21.8748' y='33.5002' width='64.1252' height='25.8751' rx='12.9375' fill={colorSet.cloud3} />
      <path
        d='M51.6875 11C65.6672 11 77 21.8291 77 35.1875C77 48.3663 65.97 59.0824 52.25 59.3682V59.375H33.125V51.6299C28.9367 47.3145 26.375 41.5372 26.375 35.1875C26.375 21.8291 37.7078 11 51.6875 11Z'
        fill={colorSet.cloud2}
      />
      <ellipse cx='33.1251' cy='40.8125' rx='19.1251' ry='18.5625' fill={colorSet.cloud1} />
      <rect x='37.6765' y='53.9308' width='11.5583' height='1.77821' rx='0.889104' transform='rotate(90 37.6765 53.9308)' fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark} />
      <rect width='11.5583' height='1.77821' rx='0.889103' transform='matrix(0.866026 -0.499999 0.5 0.866025 31.3376 61.8297)' fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark} />
      <rect x='32.4016' y='55.9517' width='11.5583' height='1.77821' rx='0.889103' transform='rotate(30 32.4016 55.9517)' fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark} />
      <rect x='67.2437' y='62' width='13.2095' height='2.03223' rx='1.01611' transform='rotate(90 67.2437 62)' fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark} />
      <rect x='59.9995' y='71.0271' width='13.2095' height='2.03223' rx='1.01611' transform='rotate(-30 59.9995 71.0271)' fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark} />
      <rect x='61.2156' y='64.3097' width='13.2095' height='2.03223' rx='1.01611' transform='rotate(30 61.2156 64.3097)' fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark} />
      <rect x='55.1497' y='74' width='14.8607' height='2.28626' rx='1.14313' transform='rotate(90 55.1497 74)' fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark} />
      <rect x='47.0002' y='84.1555' width='14.8607' height='2.28626' rx='1.14313' transform='rotate(-30 47.0002 84.1555)' fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark} />
      <rect x='48.3682' y='76.5984' width='14.8607' height='2.28626' rx='1.14313' transform='rotate(30 48.3682 76.5984)' fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark} />
    </svg>
  );
};

// 날씨 - 진눈깨비 아이콘
export const SleetSvgIcon = ({ size = 100, color, colorType = 'default', style }: IconType) => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width={size} viewBox='0 0 100 100' fill='none' style={style}>
      <rect x='21.875' y='33.5001' width='64.125' height='25.875' rx='12.9375' fill={colorSet.cloud3} />
      <path
        d='M51.6875 11C65.6672 11 77 21.8291 77 35.1875C77 48.3662 65.9699 59.0823 52.25 59.3682V59.375H33.125V51.6289C28.9369 47.3136 26.375 41.537 26.375 35.1875C26.375 21.8291 37.7078 11 51.6875 11Z'
        fill={colorSet.cloud2}
      />
      <ellipse cx='33.125' cy='40.8125' rx='19.125' ry='18.5625' fill={colorSet.cloud1} />
      <rect x='68.2439' y='62' width='13.2095' height='2.03223' rx='1.01611' transform='rotate(90 68.2439 62)' fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark} />
      <rect x='60.9998' y='71.0271' width='13.2095' height='2.03223' rx='1.01611' transform='rotate(-30 60.9998 71.0271)' fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark} />
      <rect x='62.2158' y='64.3097' width='13.2095' height='2.03223' rx='1.01611' transform='rotate(30 62.2158 64.3097)' fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark} />
      <rect x='56.1497' y='74' width='14.8607' height='2.28626' rx='1.14313' transform='rotate(90 56.1497 74)' fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark} />
      <rect x='48.0002' y='84.1555' width='14.8607' height='2.28626' rx='1.14313' transform='rotate(-30 48.0002 84.1555)' fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark} />
      <rect x='49.3682' y='76.5984' width='14.8607' height='2.28626' rx='1.14313' transform='rotate(30 49.3682 76.5984)' fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark} />
      <path
        d='M34.5359 54.7321C35.0882 53.7755 36.3114 53.4478 37.2679 54C38.2245 54.5523 38.5523 55.7755 38 56.7321L30 70.5885C29.4477 71.5451 28.2245 71.8728 27.2679 71.3206C26.3114 70.7683 25.9836 69.5451 26.5359 68.5885L34.5359 54.7321Z'
        fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark}
      />
      <path
        d='M50.7861 55.335C51.3384 54.3784 52.5616 54.0506 53.5182 54.6029C54.4748 55.1552 54.8025 56.3784 54.2502 57.335L41.7502 78.9856C41.1979 79.9422 39.9748 80.2699 39.0182 79.7177C38.0616 79.1654 37.7338 77.9422 38.2861 76.9856L50.7861 55.335Z'
        fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark}
      />
    </svg>
  );
};

// 날씨 - 소나기 아이콘
export const ShowerSvgIcon = ({ size = 100, color, colorType = 'default', style }: IconType) => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width={size} viewBox='0 0 100 100' fill='none' style={style}>
      <ellipse cx='62.4689' cy='33.8078' rx='14.0505' ry='14.0505' fill={colorSet.bright} />
      <rect x='61.2754' y='10' width='2.34174' height='5.46407' rx='1.17087' fill={colorSet.bright} />
      <rect x='61.2754' y='52.1515' width='2.34174' height='5.46407' rx='1.17087' fill={colorSet.bright} />
      <rect x='47.5054' y='15.2351' width='2.34174' height='5.46407' rx='1.17087' transform='rotate(-36 47.5054 15.2351)' fill={colorSet.bright} />
      <rect x='72.2812' y='49.3362' width='2.34174' height='5.46407' rx='1.17087' transform='rotate(-36 72.2812 49.3362)' fill={colorSet.bright} />
      <rect x='39.4419' y='27.5643' width='2.34174' height='5.46407' rx='1.17087' transform='rotate(-72 39.4419 27.5643)' fill={colorSet.bright} />
      <rect x='79.5303' y='40.5897' width='2.34174' height='5.46407' rx='1.17087' transform='rotate(-72 79.5303 40.5897)' fill={colorSet.bright} />
      <rect x='40.1655' y='42.2782' width='2.34174' height='5.46407' rx='1.17087' transform='rotate(-108 40.1655 42.2782)' fill={colorSet.bright} />
      <rect x='80.2539' y='29.2527' width='2.34174' height='5.46407' rx='1.17087' transform='rotate(-108 80.2539 29.2527)' fill={colorSet.bright} />
      <rect x='49.3999' y='53.7568' width='2.34174' height='5.46407' rx='1.17087' transform='rotate(-144 49.3999 53.7568)' fill={colorSet.bright} />
      <rect x='74.1758' y='19.6556' width='2.34174' height='5.46407' rx='1.17087' transform='rotate(-144 74.1758 19.6556)' fill={colorSet.bright} />
      <rect x='21.8574' y='43.2509' width='55.8368' height='22.5306' rx='11.2653' fill={colorSet.cloud3} />
      <path
        d='M47.8164 23.6588C59.9891 23.6589 69.8574 33.0886 69.8574 44.7203C69.8573 56.1957 60.2523 65.5253 48.3057 65.774V65.7809H31.6523V59.0358C28.006 55.2783 25.7755 50.2487 25.7754 44.7203C25.7754 33.0885 35.6436 23.6588 47.8164 23.6588Z'
        fill={colorSet.cloud2}
      />
      <ellipse cx='31.6531' cy='49.6181' rx='16.6531' ry='16.1633' fill={colorSet.cloud1} />
      <path
        d='M34.7323 60.4881C35.2068 59.6662 36.2577 59.3846 37.0796 59.8591C37.9014 60.3336 38.183 61.3845 37.7085 62.2064L30.8353 74.1111C30.3608 74.9329 29.3099 75.2145 28.4881 74.74C27.6662 74.2655 27.3847 73.2146 27.8591 72.3928L34.7323 60.4881Z'
        fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark}
      />
      <path
        d='M48.6937 61.006C49.1682 60.1842 50.2191 59.9026 51.041 60.3771C51.8628 60.8516 52.1444 61.9025 51.6699 62.7243L40.9305 81.3254C40.456 82.1473 39.4052 82.4289 38.5833 81.9544C37.7615 81.4799 37.4799 80.429 37.9544 79.6071L48.6937 61.006Z'
        fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark}
      />
      <path
        d='M50.197 83.1013C50.6715 82.2795 51.7224 81.9979 52.5442 82.4724C53.366 82.9469 53.6476 83.9978 53.1731 84.8196L51.0253 88.5398C50.5508 89.3617 49.4999 89.6433 48.678 89.1688C47.8562 88.6943 47.5746 87.6434 48.0491 86.8215L50.197 83.1013Z'
        fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark}
      />
      <path
        d='M62.6546 61.524C63.1291 60.7022 64.18 60.4206 65.0019 60.8951C65.8237 61.3696 66.1053 62.4204 65.6308 63.2423L56.6097 78.8672C56.1352 79.6891 55.0844 79.9707 54.2625 79.4962C53.4407 79.0217 53.1591 77.9708 53.6336 77.1489L62.6546 61.524Z'
        fill={colorType === 'default' ? colorSet.cool : colorSet.coolDark}
      />
    </svg>
  );
};

// 이모지 - 좋음 아이콘
export const GoodSvgIcon = ({ size = 24, color, colorType = 'default', style }: IconType) => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width={size} viewBox='0 0 48 48' fill='none'>
      <path
        d='M24 0C37.2548 0 48 10.7452 48 24C48 37.2548 37.2548 48 24 48C10.7452 48 0 37.2548 0 24C6.18487e-06 10.7452 10.7452 2.06176e-06 24 0ZM14.9795 30.4209C14.4309 29.4735 13.2189 29.1487 12.2715 29.6973C11.3242 30.2458 11.0004 31.4587 11.5479 32.4053C14.1127 36.8372 18.8836 39.5908 23.999 39.5908C29.1136 39.5908 33.8842 36.8372 36.4482 32.4053C36.9966 31.4588 36.6727 30.2459 35.7256 29.6973C34.7781 29.1486 33.5645 29.4742 33.0166 30.4209C31.1581 33.6316 27.702 35.627 23.998 35.627C20.2941 35.6269 16.8379 33.6325 14.9795 30.4209ZM14.6836 12.9541C12.4943 12.9542 10.7197 14.7297 10.7197 16.9189C10.7199 19.1081 12.4945 20.8827 14.6836 20.8828C16.8728 20.8828 18.6482 19.1081 18.6484 16.9189C18.6484 14.7296 16.873 12.9541 14.6836 12.9541ZM33.3174 12.9541C31.128 12.9541 29.3525 14.7296 29.3525 16.9189C29.3527 19.1081 31.1281 20.8828 33.3174 20.8828C35.5065 20.8827 37.2811 19.1081 37.2812 16.9189C37.2812 14.7296 35.5067 12.9542 33.3174 12.9541Z'
        fill={!color ? 'var(--text-primary)' : color}
      />
    </svg>
  );
};

// 이모지 - 보통 아이콘
export const NormalSvgIcon = ({ size = 24, color, colorType = 'default', style }: IconType) => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width={size} viewBox='0 0 48 48' fill='none'>
      <path
        d='M24 0C37.2548 0 48 10.7452 48 24C48 37.2548 37.2548 48 24 48C10.7452 48 0 37.2548 0 24C6.18487e-06 10.7452 10.7452 0 24 0ZM13.2666 31.2148C12.1718 31.215 11.2852 32.1025 11.2852 33.1973C11.2853 34.292 12.1719 35.1785 13.2666 35.1787H34.7363C35.8311 35.1786 36.7186 34.292 36.7188 33.1973C36.7188 32.1024 35.8312 31.2149 34.7363 31.2148H13.2666ZM14.6836 12.9541C12.4943 12.9542 10.7197 14.7287 10.7197 16.918C10.7198 19.1072 12.4944 20.8817 14.6836 20.8818C16.8729 20.8818 18.6484 19.1073 18.6484 16.918C18.6484 14.7286 16.873 12.9541 14.6836 12.9541ZM33.3174 12.9541C31.128 12.9541 29.3525 14.7286 29.3525 16.918C29.3526 19.1073 31.1281 20.8818 33.3174 20.8818C35.5066 20.8817 37.2812 19.1072 37.2812 16.918C37.2812 14.7287 35.5067 12.9542 33.3174 12.9541Z'
        fill={!color ? 'var(--text-primary)' : color}
      />
    </svg>
  );
};

// 이모지 - 나쁨 아이콘
export const BadSvgIcon = ({ size = 24, color, colorType = 'default', style }: IconType) => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width={size} viewBox='0 0 48 48' fill='none'>
      <path
        d='M24 0C37.2548 0 48 10.7452 48 24C48 37.2548 37.2548 48 24 48C10.7452 48 0 37.2548 0 24C6.19888e-06 10.7452 10.7452 0 24 0ZM24.001 26.5986C18.8865 26.5988 14.1165 29.3515 11.5518 33.7842C11.0031 34.7308 11.327 35.9435 12.2744 36.4922C13.2209 37.04 14.4337 36.7167 14.9824 35.7695C16.8408 32.5586 20.2962 30.5626 24.001 30.5625C27.7059 30.5625 31.1611 32.5577 33.0195 35.7695C33.3874 36.4045 34.0531 36.7588 34.7373 36.7588C35.0742 36.7588 35.417 36.6721 35.7285 36.4922C36.6757 35.9435 36.9989 34.7307 36.4512 33.7842C33.8863 29.3522 29.1156 26.5986 24.001 26.5986ZM14.6836 13.2236C12.4942 13.2236 10.7188 14.9981 10.7188 17.1875C10.7188 19.3768 12.4943 21.1514 14.6836 21.1514C16.8727 21.1511 18.6474 19.3766 18.6475 17.1875C18.6475 14.9983 16.8727 13.2239 14.6836 13.2236ZM33.3164 13.2236C31.1271 13.2237 29.3525 14.9982 29.3525 17.1875C29.3526 19.3768 31.1271 21.1513 33.3164 21.1514C35.5057 21.1514 37.2802 19.3768 37.2803 17.1875C37.2803 14.9981 35.5058 13.2236 33.3164 13.2236Z'
        fill={!color ? 'var(--text-primary)' : color}
      />
    </svg>
  );
};

// 이모지 - 매우나쁨 아이콘
export const VeryBadSvgIcon = ({ size = 24, color, colorType = 'default', style }: IconType) => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width={size} viewBox='0 0 48 48' fill='none'>
      <path
        d='M24 0C37.2548 0 48 10.7452 48 24C48 37.2548 37.2548 48 24 48C10.7452 48 0 37.2548 0 24C6.18398e-06 10.7452 10.7452 0 24 0ZM24 24.6797C20.2781 24.6797 17.2607 28.4937 17.2607 33.1982C17.2609 37.9025 20.2782 41.7158 24 41.7158C27.7218 41.7158 30.7391 37.9025 30.7393 33.1982C30.7393 28.4938 27.7219 24.6797 24 24.6797ZM14.6836 12.9541C12.4943 12.9541 10.7198 14.7286 10.7197 16.918C10.7197 19.1073 12.4942 20.8818 14.6836 20.8818C16.8729 20.8818 18.6475 19.1073 18.6475 16.918C18.6474 14.7287 16.8729 12.9542 14.6836 12.9541ZM33.3164 12.9541C31.1273 12.9543 29.3526 14.7288 29.3525 16.918C29.3525 19.1072 31.1272 20.8816 33.3164 20.8818C35.5058 20.8818 37.2812 19.1073 37.2812 16.918C37.2812 14.7286 35.5057 12.9541 33.3164 12.9541Z'
        fill={!color ? 'var(--text-primary)' : color}
      />
    </svg>
  );
};
